import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    Spinner,
    Alert,
    ListGroup,
    Image,
    Tabs,
    Tab,
    Table,
    Form,
    Modal,
    Button,
    Badge,
} from "react-bootstrap";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { getMatchHandicap } from "../utils/handicap";
import { getYouTubeEmbedUrl } from "../utils/youtube";
import { getLevelChipClass, getLevelTextStyle } from "../utils/levelColors";
import { isWalkover, getWalkoverLabel } from "../utils/walkover";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    Filler
);

const API_URL = process.env.REACT_APP_API_URL;

// ========== CHART COLORS (đồng bộ với PlayerDetail) ==========
const CHART_COLORS = {
    success: "rgba(34, 197, 94, 0.8)",    // Green-500
    successBorder: "rgb(34, 197, 94)",
    danger: "rgba(239, 68, 68, 0.8)",     // Red-500
    dangerBorder: "rgb(239, 68, 68)",
    primary: "rgba(59, 130, 246, 0.8)",   // Blue-500 (Accent)
    primaryBorder: "rgb(59, 130, 246)",
    info: "rgba(30, 41, 59, 0.8)",        // Slate-800 (Primary)
    infoBorder: "rgb(30, 41, 59)",
    singleGreen: "rgba(34, 197, 94, 0.8)",
    doublePurple: "rgba(168, 85, 247, 0.8)", // Purple-500
};

const LEVEL_MAP = {
    8: "H", 7: "G", 1: "F", 2: "E", 3: "D", 4: "C", 5: "B", 6: "A",
};

const TeamDetail = () => {
    const { id } = useParams();
    const [team, setTeam] = useState(null);
    const [members, setMembers] = useState([]);
    const [matchHistory, setMatchHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [seasons, setSeasons] = useState([]);
    const [selectedSeason, setSelectedSeason] = useState("");
    const [filteredMatchHistory, setFilteredMatchHistory] = useState([]);
    const [detailedMatches, setDetailedMatches] = useState({});
    const [showMatchDetailModal, setShowMatchDetailModal] = useState(false);
    const [currentMatchDetails, setCurrentMatchDetails] = useState(null);
    const [currentMatchInfo, setCurrentMatchInfo] = useState(null);
    const [allSubMatches, setAllSubMatches] = useState([]);
    const [isStatLoading, setIsStatLoading] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [currentVideoUrl, setCurrentVideoUrl] = useState("");
    const [allPlayersLevelHistoryMap, setAllPlayersLevelHistoryMap] = useState({});

    const getLevelNameFromId = useCallback(
        (levelId) => LEVEL_MAP[levelId] || `ID ${levelId}`,
        []
    );

    const getPlayerLevelAtDate = useCallback(
        (playerLevelHistory, matchDate, currentLevelId) => {
            const matchTimestamp = new Date(matchDate).getTime();
            const sortedHistory = [...playerLevelHistory].sort(
                (a, b) =>
                    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );

            if (sortedHistory.length === 0) return currentLevelId;

            let historicalLevelId = currentLevelId;
            for (const levelChange of sortedHistory) {
                const changeTimestamp = new Date(levelChange.created_at).getTime();
                if (changeTimestamp <= matchTimestamp) {
                    historicalLevelId = levelChange.level_new;
                } else {
                    break;
                }
            }

            if (
                historicalLevelId === currentLevelId &&
                sortedHistory.length > 0 &&
                new Date(sortedHistory[0].created_at).getTime() > matchTimestamp
            ) {
                historicalLevelId = sortedHistory[0].level_old;
            }

            return historicalLevelId;
        },
        []
    );

    useEffect(() => {
        const fetchTeamDetails = async () => {
            try {
                const [teamRes, membersRes, matchHistoryRes] = await Promise.all([
                    axios.get(`${API_URL}/teamspublic/${id}`),
                    axios.get(`${API_URL}/teamspublic/${id}/members`),
                    axios.get(`${API_URL}/teamspublic/${id}/match-history`),
                ]);
                setTeam(teamRes.data);
                setMembers(membersRes.data);
                setMatchHistory(matchHistoryRes.data);
            } catch (err) {
                console.error("Error fetching team details:", err);
                setError("Failed to load team details.");
            } finally {
                setLoading(false);
            }
        };
        fetchTeamDetails();
    }, [id]);

    useEffect(() => {
        const fetchSeasons = async () => {
            try {
                const response = await axios.get(`${API_URL}/seasons`);
                setSeasons(response.data);
                if (response.data.length > 0) {
                    setSelectedSeason(response.data[0].id.toString());
                }
            } catch (err) {
                console.error("Error fetching seasons:", err);
            }
        };
        fetchSeasons();
    }, []);

    useEffect(() => {
        if (matchHistory.length > 0) {
            const filtered = matchHistory.filter(
                (match) =>
                    !selectedSeason ||
                    (match.season_id && match.season_id.toString() === selectedSeason)
            );
            setFilteredMatchHistory(filtered);
        }
    }, [matchHistory, selectedSeason]);

    useEffect(() => {
        const fetchAllSubMatches = async () => {
            if (filteredMatchHistory.length === 0) {
                setAllSubMatches([]);
                return;
            }
            setIsStatLoading(true);
            try {
                const subMatchPromises = filteredMatchHistory.map((match) =>
                    axios.get(`${API_URL}/details/match-details/${match.schedule_id}`)
                );
                const subMatchResults = await Promise.all(subMatchPromises);
                const allDetails = subMatchResults.flatMap((res, index) => {
                    const parentMatch = filteredMatchHistory[index];
                    return res.data.map((subMatch) => ({
                        ...subMatch,
                        parent_match_date: parentMatch.play_date,
                        parent_home_team_id: parentMatch.home,
                        parent_guest_team_id: parentMatch.guest,
                    }));
                });
                setAllSubMatches(allDetails);
            } catch (err) {
                console.error("Error fetching all sub-match details:", err);
            } finally {
                setIsStatLoading(false);
            }
        };

        fetchAllSubMatches();
    }, [filteredMatchHistory]);

    const teamStats = useMemo(() => {
        if (!team || filteredMatchHistory.length === 0) return null;

        const monthlyStatsData = {};
        const overallStats = { wins: 0, losses: 0 };

        filteredMatchHistory.forEach((match) => {
            const date = new Date(match.play_date);
            const month = date.toISOString().slice(0, 7);
            if (!monthlyStatsData[month]) {
                monthlyStatsData[month] = {
                    wins: 0,
                    losses: 0,
                    label: date.toLocaleDateString("vi-VN", { month: "2-digit", year: "numeric" })
                };
            }
            const isHome = match.home === team.id;

            if (match.home_result > match.guest_result) {
                if (isHome) {
                    monthlyStatsData[month].wins++;
                    overallStats.wins++;
                } else {
                    monthlyStatsData[month].losses++;
                    overallStats.losses++;
                }
            } else if (match.home_result < match.guest_result) {
                if (isHome) {
                    monthlyStatsData[month].losses++;
                    overallStats.losses++;
                } else {
                    monthlyStatsData[month].wins++;
                    overallStats.wins++;
                }
            }
        });

        const monthlyStats = Object.values(monthlyStatsData)
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(month => ({
                ...month,
                winRate: ((month.wins / (month.wins + month.losses)) * 100) || 0
            }));

        const subMatchStats = {
            singles: { wins: 0, total: 0 },
            doubles: { wins: 0, total: 0 },
        };

        allSubMatches.forEach((subMatch) => {
            const isDouble = subMatch.p2_name && subMatch.p4_name;
            const isTeamInHome = subMatch.parent_home_team_id === team.id;
            const didWin = isTeamInHome
                ? subMatch.home_result > subMatch.guest_result
                : subMatch.guest_result > subMatch.home_result;

            if (isDouble) {
                subMatchStats.doubles.total++;
                if (didWin) subMatchStats.doubles.wins++;
            } else {
                subMatchStats.singles.total++;
                if (didWin) subMatchStats.singles.wins++;
            }
        });

        const singlesWinRate = ((subMatchStats.singles.wins / subMatchStats.singles.total) * 100) || 0;
        const doublesWinRate = ((subMatchStats.doubles.wins / subMatchStats.doubles.total) * 100) || 0;

        return {
            monthly: monthlyStats,
            overall: overallStats,
            subMatches: subMatchStats,
            subMatchWinRates: {
                singles: singlesWinRate,
                doubles: doublesWinRate,
            },
        };
    }, [team, filteredMatchHistory, allSubMatches]);

    const handleShowMatchDetails = useCallback(async (scheduleId, matchInfo) => {
        setShowMatchDetailModal(true);
        setCurrentMatchDetails(null);
        setCurrentMatchInfo(matchInfo);

        if (detailedMatches[scheduleId]) {
            setCurrentMatchDetails(detailedMatches[scheduleId]);
        } else {
            try {
                const response = await axios.get(
                    `${API_URL}/details/match-details/${scheduleId}`,
                );
                setDetailedMatches((prevDetails) => ({
                    ...prevDetails,
                    [scheduleId]: response.data,
                }));
                setCurrentMatchDetails(response.data);

                // Fetch level history cho tất cả players trong match details
                const uniquePlayerIds = new Set();
                response.data.forEach((detail) => {
                    [detail.player_1, detail.player_2, detail.player_3, detail.player_4].forEach(
                        (pid) => {
                            if (pid && !allPlayersLevelHistoryMap[pid]) {
                                uniquePlayerIds.add(pid);
                            }
                        }
                    );
                });

                if (uniquePlayerIds.size > 0) {
                    const levelHistories = await Promise.all(
                        Array.from(uniquePlayerIds).map((pid) =>
                            axios
                                .get(`${API_URL}/playerspublic/${pid}/level-history`)
                                .then((res) => ({ playerId: pid, history: res.data }))
                                .catch(() => ({ playerId: pid, history: [] }))
                        )
                    );

                    const historyMap = levelHistories.reduce((acc, { playerId, history }) => {
                        acc[playerId] = history;
                        return acc;
                    }, {});

                    setAllPlayersLevelHistoryMap((prev) => ({ ...prev, ...historyMap }));
                }
            } catch (err) {
                console.error(
                    `Error fetching detailed matches for schedule ${scheduleId}:`,
                    err,
                );
                setCurrentMatchDetails([]);
            }
        }
    }, [detailedMatches, allPlayersLevelHistoryMap]);

    const handleCloseMatchDetailModal = useCallback(() => {
        setShowMatchDetailModal(false);
        setCurrentMatchDetails(null);
        setCurrentMatchInfo(null);
    }, []);

    const filteredMembers = useMemo(() => {
        return members.filter(
            (member) =>
                !selectedSeason ||
                (member.season_id && member.season_id.toString() === selectedSeason)
        );
    }, [members, selectedSeason]);

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Đang tải...</span>
                </Spinner>
                <p className="mt-3">Đang tải thông tin đội...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger" className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    Không thể tải chi tiết đội.
                </Alert>
            </Container>
        );
    }

    if (!team) {
        return (
            <Container className="mt-5">
                <Alert variant="info" className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    Không tìm thấy đội.
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <div className="mb-4">
                <h1 className="fw-bold mb-1">Hồ Sơ Đội Bóng</h1>
                <p className="text-muted small">Thông tin chi tiết và thành tích của {team.name}</p>
            </div>

            {/* Team Info Card */}
            <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                <Card.Body className="p-4">
                    <Row className="align-items-center">
                        <Col md={3} xs={12} className="text-center mb-3 mb-md-0">
                            {team.logo ? (
                                <Image
                                    src={team.logo}
                                    roundedCircle
                                    style={{
                                        width: "180px",
                                        height: "180px",
                                        objectFit: "cover",
                                        border: "4px solid #fff",
                                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: "180px",
                                        height: "180px",
                                        borderRadius: "50%",
                                        backgroundColor: "#f8f9fa",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "4rem",
                                        color: "var(--primary-color)",
                                        border: "4px solid #fff",
                                        fontWeight: 600,
                                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                                    }}
                                >
                                    {team.name.charAt(0)}
                                </div>
                            )}
                        </Col>
                        <Col md={9} xs={12}>
                            <h2 className="mb-4 fw-bold">
                                {team.name}
                            </h2>
                            <Row>
                                <Col md={6} xs={12}>
                                    <ListGroup variant="flush">
                                        <ListGroup.Item className="border-0 px-0 py-2">
                                            <div className="text-muted small text-uppercase fw-bold mb-1">CLB Chủ Quản</div>
                                            <div className="text-muted ps-3 border-start border-3 border-primary">
                                                {team.club}
                                            </div>
                                        </ListGroup.Item>
                                        <ListGroup.Item className="border-0 px-0 py-2">
                                            <div className="text-muted small text-uppercase fw-bold mb-1">Sân Vận Động & Liên Hệ</div>
                                            <div className="text-muted ps-3 border-start border-3 border-success" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                                                {team.stadium}
                                            </div>
                                        </ListGroup.Item>
                                    </ListGroup>
                                </Col>
                                <Col md={6} xs={12}>
                                    <ListGroup variant="flush">
                                        <ListGroup.Item className="border-0 px-0 py-2 d-flex align-items-center justify-content-between">
                                            <div className="text-muted small text-uppercase fw-bold">Hạng Đấu</div>
                                            <span className="level-text" style={{ fontSize: '1.1rem', ...getLevelTextStyle(team.level_team_name) }}>
                                                {team.level_team_name}
                                            </span>
                                        </ListGroup.Item>
                                    </ListGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Season Selector */}
            <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                <Card.Body className="p-4">
                    <Row className="align-items-center">
                        <Col md={3} xs={12} className="mb-2 mb-md-0">
                            <p className="text-muted small text-uppercase fw-bold mb-0">
                                Chọn Mùa Giải
                            </p>
                        </Col>
                        <Col md={9} xs={12}>
                            <Form.Select
                                value={selectedSeason}
                                onChange={(e) => setSelectedSeason(e.target.value)}
                                style={{ borderRadius: '8px' }}
                            >
                                <option value="">Tất cả mùa giải</option>
                                {seasons.map((season) => (
                                    <option key={season.id} value={season.id}>
                                        {season.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Stats Cards */}
            {teamStats && (
                <Row className="mb-4 g-3">
                    <Col md={4} xs={12}>
                        <Card className="text-center border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                            <Card.Body className="py-4">
                                <p className="text-muted small text-uppercase fw-bold mb-2">Tổng Thắng</p>
                                <h2 className="fw-bold mb-0">
                                    {teamStats.overall.wins}
                                </h2>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4} xs={12}>
                        <Card className="text-center border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                            <Card.Body className="py-4">
                                <p className="text-muted small text-uppercase fw-bold mb-2">Tổng Thua</p>
                                <h2 className="fw-bold mb-0">
                                    {teamStats.overall.losses}
                                </h2>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4} xs={12}>
                        <Card className="text-center border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                            <Card.Body className="py-4">
                                <p className="text-muted small text-uppercase fw-bold mb-2">Tỷ Lệ Thắng</p>
                                <h2 className="fw-bold mb-0">
                                    {teamStats.overall.wins + teamStats.overall.losses > 0
                                        ? ((teamStats.overall.wins / (teamStats.overall.wins + teamStats.overall.losses)) * 100).toFixed(1)
                                        : 0}%
                                </h2>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Tabs */}
            <Tabs
                defaultActiveKey="members"
                id="team-details-tabs"
                className="custom-tabs-minimal"
            >
                <Tab
                    eventKey="members"
                    title="Thành Viên"
                    tabClassName="fw-semibold"
                >
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-4">
                            {filteredMembers.length > 0 ? (
                                <Row xs={1} md={2} lg={3} xl={4} className="g-4">
                                    {filteredMembers.map((member, index) => (
                                        <Col key={index}>
                                            <Link
                                                to={`/players/${member.player_id}`}
                                                style={{ textDecoration: 'none' }}
                                            >
                                                <Card
                                                    className="h-100 border-0 shadow-sm hover-shadow"
                                                    style={{ borderRadius: '12px', transition: 'all 0.3s ease' }}
                                                >
                                                    <Card.Body className="p-3">
                                                        <div className="text-center mb-3">
                                                            {member.player_image ? (
                                                                <Image
                                                                    src={`https://s3.cloudfly.vn/player-images/player/${member.player_image}`}
                                                                    roundedCircle
                                                                    style={{
                                                                        width: "100px",
                                                                        height: "100px",
                                                                        objectFit: "cover",
                                                                        border: "3px solid #e67e22",
                                                                        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.08)",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div
                                                                    style={{
                                                                        width: "100px",
                                                                        height: "100px",
                                                                        borderRadius: "50%",
                                                                        backgroundColor: "#f8f9fa",
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: "2rem",
                                                                        color: "#e67e22",
                                                                        border: "3px solid #e67e22",
                                                                        fontWeight: "bold",
                                                                        margin: "0 auto",
                                                                        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.08)",
                                                                    }}
                                                                >
                                                                    {member.player_name.charAt(0)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <h6 className="text-center mb-3 fw-bold" style={{ color: '#e67e22' }}>
                                                            {member.player_name}
                                                        </h6>
                                                        <ListGroup variant="flush" className="small">
                                                            <ListGroup.Item className="d-flex justify-content-between align-items-center px-0 border-0">
                                                                <span>Năm Sinh:</span>
                                                                <Badge bg="secondary" style={{ borderRadius: '6px' }}>
                                                                    {member.date_of_birth
                                                                        ? new Date(member.date_of_birth).getFullYear()
                                                                        : "N/A"}
                                                                </Badge>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="d-flex justify-content-between align-items-center px-0 border-0">
                                                                <span>Hạng:</span>
                                                                <span className={getLevelChipClass(member.level_name)}>
                                                                    {member.level_name || "N/A"}
                                                                </span>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="d-flex justify-content-between align-items-center px-0 border-0">
                                                                <span>Điểm Mùa:</span>
                                                                <Badge bg="primary" style={{ borderRadius: '6px' }}>
                                                                    {member.season_points !== null
                                                                        ? member.season_points
                                                                        : "0"}
                                                                </Badge>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="d-flex justify-content-between align-items-center px-0 border-0">
                                                                <span>Điểm Hạng:</span>
                                                                <Badge bg="success" style={{ borderRadius: '6px' }}>
                                                                    {member.level_point !== null
                                                                        ? member.level_point
                                                                        : "0"}
                                                                </Badge>
                                                            </ListGroup.Item>
                                                        </ListGroup>
                                                    </Card.Body>
                                                </Card>
                                            </Link>
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <Alert variant="info" className="mb-0 border-0" style={{ borderRadius: '12px' }}>
                                    Không tìm thấy thành viên nào cho mùa giải đã chọn.
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
                <Tab
                    eventKey="matchHistory"
                    title="Lịch Sử Thi Đấu"
                    tabClassName="fw-semibold"
                >
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                        <Card.Body className="p-0">
                            {filteredMatchHistory.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="mb-0">
                                        <thead className="bg-white border-bottom">
                                            <tr>
                                                <th className="text-center" style={{ width: "50px" }}>
                                                    #
                                                </th>
                                                <th className="text-center" style={{ width: "120px" }}>Ngày</th>
                                                <th className="text-center" style={{ width: "150px" }}>Vòng Đấu</th>
                                                <th className="text-center">Đội Nhà</th>
                                                <th className="text-center" style={{ width: "100px" }}>
                                                    Tỷ Số
                                                </th>
                                                <th className="text-center">Đội Khách</th>
                                                <th className="text-center" style={{ width: "100px" }}>
                                                    Chi tiết
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredMatchHistory.map((match, index) => {
                                                const teamName = team.name;
                                                const isTeamHome = match.home_team_name === teamName;
                                                let isWin, isLoss;

                                                if (isTeamHome) {
                                                    isWin = match.home_result > match.guest_result;
                                                    isLoss = match.home_result < match.guest_result;
                                                } else {
                                                    isWin = match.guest_result > match.home_result;
                                                    isLoss = match.guest_result < match.home_result;
                                                }

                                                return (
                                                    <tr key={match.schedule_id} className="align-middle">
                                                        <td className="text-center fw-bold text-muted">
                                                            {index + 1}
                                                        </td>
                                                        <td className="text-center">
                                                            <small>
                                                                {new Date(match.play_date).toLocaleDateString("vi-VN")}
                                                            </small>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="secondary" style={{ borderRadius: '6px' }}>
                                                                {match.round_type === 1
                                                                    ? `Vòng Bảng ${match.round_id || ""}`
                                                                    : match.round_type === 2
                                                                        ? "Vòng 1/16"
                                                                        : match.round_type === 3
                                                                            ? "Vòng 1/8"
                                                                            : match.round_type === 4
                                                                                ? "Tứ kết"
                                                                                : match.round_type === 5
                                                                                    ? "Bán kết"
                                                                                    : match.round_type === 6
                                                                                        ? "Chung kết"
                                                                                        : "N/A"}
                                                            </Badge>
                                                        </td>
                                                        <td
                                                            className={`text-center ${isTeamHome ? "fw-bold" : ""}`}
                                                            style={isTeamHome ? { color: '#e67e22' } : {}}
                                                        >
                                                            {match.home ? (
                                                                <Link
                                                                    to={`/teams/${match.home}`}
                                                                    style={{
                                                                        textDecoration: 'none',
                                                                        color: 'inherit',
                                                                        fontWeight: isTeamHome ? 'bold' : 'normal',
                                                                        transition: 'color 0.2s ease'
                                                                    }}
                                                                    onMouseEnter={(e) => !isTeamHome && (e.currentTarget.style.color = '#e67e22')}
                                                                    onMouseLeave={(e) => !isTeamHome && (e.currentTarget.style.color = 'inherit')}
                                                                >
                                                                    {match.home_team_name}
                                                                </Link>
                                                            ) : (
                                                                match.home_team_name
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge
                                                                bg={isWin ? "success" : "danger"}
                                                                className="fs-6 px-3"
                                                                style={{ borderRadius: '8px' }}
                                                            >
                                                                {match.home_result} - {match.guest_result}
                                                            </Badge>
                                                        </td>
                                                        <td
                                                            className={`text-center ${!isTeamHome ? "fw-bold" : ""}`}
                                                            style={!isTeamHome ? { color: '#e67e22' } : {}}
                                                        >
                                                            {match.guest ? (
                                                                <Link
                                                                    to={`/teams/${match.guest}`}
                                                                    style={{
                                                                        textDecoration: 'none',
                                                                        color: 'inherit',
                                                                        fontWeight: !isTeamHome ? 'bold' : 'normal',
                                                                        transition: 'color 0.2s ease'
                                                                    }}
                                                                    onMouseEnter={(e) => isTeamHome && (e.currentTarget.style.color = '#e67e22')}
                                                                    onMouseLeave={(e) => isTeamHome && (e.currentTarget.style.color = 'inherit')}
                                                                >
                                                                    {match.guest_team_name}
                                                                </Link>
                                                            ) : (
                                                                match.guest_team_name
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() =>
                                                                    handleShowMatchDetails(match.schedule_id, {
                                                                        isTeamHome,
                                                                        homeTeamName: match.home_team_name,
                                                                        guestTeamName: match.guest_team_name,
                                                                    })
                                                                }
                                                                style={{ borderRadius: '6px' }}
                                                            >
                                                                Xem
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <Alert variant="info" className="m-4 border-0" style={{ borderRadius: '12px' }}>
                                    Không tìm thấy lịch sử thi đấu nào cho mùa giải đã chọn.
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
                <Tab eventKey="stats" title="Thống Kê" tabClassName="fw-semibold">
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-4">
                            {isStatLoading ? (
                                <div className="text-center">
                                    <Spinner animation="border" />
                                    <p>Đang tải dữ liệu thống kê...</p>
                                </div>
                            ) : teamStats ? (
                                <Row className="g-4">
                                    <Col lg={6}>
                                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                                            <Card.Body className="p-4">
                                                <h5 className="fw-bold mb-4" style={{ color: '#e67e22' }}>
                                                    Xu Hướng Tỷ Lệ Thắng
                                                </h5>
                                                <div style={{ height: "300px" }}>
                                                    <Line
                                                        data={{
                                                            labels: teamStats.monthly.map(item => item.label),
                                                            datasets: [
                                                                {
                                                                    label: "Tỷ Lệ Thắng %",
                                                                    data: teamStats.monthly.map(item => item.winRate),
                                                                    borderColor: CHART_COLORS.primaryBorder,
                                                                    backgroundColor: "rgba(230, 126, 34, 0.1)",
                                                                    borderWidth: 3,
                                                                    fill: true,
                                                                    tension: 0.4,
                                                                    pointRadius: 6,
                                                                    pointBackgroundColor: CHART_COLORS.primaryBorder,
                                                                    pointBorderColor: "#fff",
                                                                    pointBorderWidth: 2,
                                                                },
                                                            ],
                                                        }}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            plugins: { legend: { display: false } },
                                                            scales: {
                                                                y: { beginAtZero: true, max: 100, ticks: { callback: (value) => `${value}%` } },
                                                                x: { grid: { display: false } },
                                                            },
                                                        }}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col lg={6}>
                                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                                            <Card.Body className="p-4">
                                                <h5 className="fw-bold mb-4" style={{ color: '#e67e22' }}>
                                                    Thắng/Thua
                                                </h5>
                                                <div style={{ height: "300px" }}>
                                                    <Doughnut
                                                        data={{
                                                            labels: ["Thắng", "Thua"],
                                                            datasets: [
                                                                {
                                                                    data: [
                                                                        teamStats.overall.wins,
                                                                        teamStats.overall.losses,
                                                                    ],
                                                                    backgroundColor: [
                                                                        CHART_COLORS.success,
                                                                        CHART_COLORS.danger,
                                                                    ],
                                                                    borderColor: [
                                                                        CHART_COLORS.successBorder,
                                                                        CHART_COLORS.dangerBorder,
                                                                    ],
                                                                    borderWidth: 2,
                                                                },
                                                            ],
                                                        }}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            plugins: {
                                                                legend: { position: "bottom", labels: { font: { size: 14 }, padding: 15 } },
                                                            },
                                                        }}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col lg={12}>
                                        <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                                            <Card.Body className="p-4">
                                                <h5 className="fw-bold mb-4" style={{ color: '#e67e22' }}>
                                                    Kết Quả Theo Tháng
                                                </h5>
                                                <div style={{ height: "300px" }}>
                                                    <Bar
                                                        data={{
                                                            labels: teamStats.monthly.map(m => m.label),
                                                            datasets: [
                                                                {
                                                                    label: 'Thắng',
                                                                    data: teamStats.monthly.map(m => m.wins),
                                                                    backgroundColor: CHART_COLORS.success,
                                                                    borderRadius: 8,
                                                                },
                                                                {
                                                                    label: 'Thua',
                                                                    data: teamStats.monthly.map(m => m.losses),
                                                                    backgroundColor: CHART_COLORS.danger,
                                                                    borderRadius: 8,
                                                                },
                                                            ],
                                                        }}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            plugins: { legend: { position: 'top' } },
                                                            scales: {
                                                                x: { grid: { display: false } },
                                                                y: { beginAtZero: true },
                                                            },
                                                        }}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col lg={12}>
                                        <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                                            <Card.Body className="p-4">
                                                <h5 className="fw-bold mb-4" style={{ color: '#e67e22' }}>
                                                    ⭐ So Sánh % Thắng Trận Đơn & Đôi
                                                </h5>
                                                <div style={{ height: "300px" }}>
                                                    <Bar
                                                        data={{
                                                            labels: ['Tỷ lệ thắng'],
                                                            datasets: [
                                                                {
                                                                    label: 'Đơn',
                                                                    data: [teamStats.subMatchWinRates.singles],
                                                                    backgroundColor: CHART_COLORS.singleGreen,
                                                                    barThickness: 100,
                                                                    borderRadius: 8,
                                                                },
                                                                {
                                                                    label: 'Đôi',
                                                                    data: [teamStats.subMatchWinRates.doubles],
                                                                    backgroundColor: CHART_COLORS.doublePurple,
                                                                    barThickness: 100,
                                                                    borderRadius: 8,
                                                                },
                                                            ],
                                                        }}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            indexAxis: 'y',
                                                            plugins: { legend: { position: 'top' } },
                                                            scales: {
                                                                x: {
                                                                    max: 100,
                                                                    ticks: { callback: (value) => `${value}%` },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            ) : (
                                <Alert variant="info">Không có dữ liệu thống kê cho mùa giải này.</Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>

            {/* Match Details Modal */}
            <Modal
                show={showMatchDetailModal}
                onHide={handleCloseMatchDetailModal}
                size="xl"
                centered
            >
                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor: '#e67e22',
                        color: 'white',
                        borderRadius: '12px 12px 0 0'
                    }}
                >
                    <Modal.Title className="fw-bold">
                        Chi tiết trận đấu
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {currentMatchDetails ? (
                        currentMatchDetails.length > 0 ? (
                            <div className="table-responsive" style={{ borderRadius: '8px', overflow: 'hidden', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                <Table striped hover className="mb-0" style={{ minWidth: '800px' }}>
                                    <thead style={{ backgroundColor: '#2c3e50', color: 'white' }}>
                                        <tr>
                                            <th className="text-center" style={{ width: "80px" }}>
                                                Trận
                                            </th>
                                            <th>
                                                VĐV 1 ({currentMatchInfo?.isTeamHome ? "Nhà" : "Khách"})
                                            </th>
                                            <th>
                                                VĐV 2 ({currentMatchInfo?.isTeamHome ? "Nhà" : "Khách"})
                                            </th>
                                            <th>
                                                VĐV 3 ({currentMatchInfo?.isTeamHome ? "Khách" : "Nhà"})
                                            </th>
                                            <th>
                                                VĐV 4 ({currentMatchInfo?.isTeamHome ? "Khách" : "Nhà"})
                                            </th>
                                            <th className="text-center" style={{ width: "120px" }}>
                                                Kết quả
                                            </th>
                                            <th className="text-center" style={{ width: "80px" }}>
                                                Video
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentMatchDetails.map((detail, detailIndex) => {
                                            const isMatchWin = currentMatchInfo?.isTeamHome
                                                ? detail.home_result > detail.guest_result
                                                : detail.guest_result > detail.home_result;

                                            // Lấy play_date từ parent match
                                            const matchDate = filteredMatchHistory.find(
                                                m => m.schedule_id === detail.schedule_id
                                            )?.play_date;

                                            // Tính hạng chính xác tại thời điểm trận đấu cho từng player
                                            const p1HistoricalLevel = detail.player_1 && matchDate
                                                ? getLevelNameFromId(
                                                    getPlayerLevelAtDate(
                                                        allPlayersLevelHistoryMap[detail.player_1] || [],
                                                        matchDate,
                                                        detail.p1_level
                                                    )
                                                )
                                                : getLevelNameFromId(detail.p1_level);

                                            const p2HistoricalLevel = detail.player_2 && matchDate
                                                ? getLevelNameFromId(
                                                    getPlayerLevelAtDate(
                                                        allPlayersLevelHistoryMap[detail.player_2] || [],
                                                        matchDate,
                                                        detail.p2_level
                                                    )
                                                )
                                                : getLevelNameFromId(detail.p2_level);

                                            const p3HistoricalLevel = detail.player_3 && matchDate
                                                ? getLevelNameFromId(
                                                    getPlayerLevelAtDate(
                                                        allPlayersLevelHistoryMap[detail.player_3] || [],
                                                        matchDate,
                                                        detail.p3_level
                                                    )
                                                )
                                                : getLevelNameFromId(detail.p3_level);

                                            const p4HistoricalLevel = detail.player_4 && matchDate
                                                ? getLevelNameFromId(
                                                    getPlayerLevelAtDate(
                                                        allPlayersLevelHistoryMap[detail.player_4] || [],
                                                        matchDate,
                                                        detail.p4_level
                                                    )
                                                )
                                                : getLevelNameFromId(detail.p4_level);

                                            return (
                                                <tr key={detailIndex}>
                                                    <td className="text-center fw-bold">
                                                        <Badge bg="secondary" className="fs-6 px-3 py-2" style={{ borderRadius: '8px' }}>
                                                            #{detail.math_index}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {isWalkover(detail.walkover) ? (
                                                            <Badge bg="warning" text="dark">Bỏ cuộc</Badge>
                                                        ) : detail.player_1 ? (
                                                            <div>
                                                                <Link
                                                                    to={`/players/${detail.player_1}`}
                                                                    style={{
                                                                        textDecoration: 'none',
                                                                        fontWeight: '600',
                                                                        color: 'inherit',
                                                                        transition: 'color 0.2s ease'
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.color = '#e67e22'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                                                                >
                                                                    {detail.p1_name || "-"}
                                                                </Link>
                                                                <div className="d-flex align-items-center mt-1" style={{ fontSize: '0.8rem' }}>
                                                                    <Badge bg="secondary" className="me-1 fw-normal" style={{ fontSize: '0.75rem' }}>
                                                                        {p1HistoricalLevel || "?"}
                                                                    </Badge>
                                                                    <span className="text-muted">
                                                                        {detail.point_player_1 !== null ? detail.point_player_1 : "-"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            detail.p1_name || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {isWalkover(detail.walkover) ? (
                                                            <span className="text-muted">-</span>
                                                        ) : detail.player_2 ? (
                                                            <div>
                                                                <Link
                                                                    to={`/players/${detail.player_2}`}
                                                                    style={{
                                                                        textDecoration: 'none',
                                                                        fontWeight: '600',
                                                                        color: 'inherit',
                                                                        transition: 'color 0.2s ease'
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.color = '#e67e22'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                                                                >
                                                                    {detail.p2_name || "-"}
                                                                </Link>
                                                                <div className="d-flex align-items-center mt-1" style={{ fontSize: '0.8rem' }}>
                                                                    <Badge bg="secondary" className="me-1 fw-normal" style={{ fontSize: '0.75rem' }}>
                                                                        {p2HistoricalLevel || "?"}
                                                                    </Badge>
                                                                    <span className="text-muted">
                                                                        {detail.point_player_2 !== null ? detail.point_player_2 : "-"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            detail.p2_name || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {isWalkover(detail.walkover) ? (
                                                            <Badge bg="warning" text="dark">Bỏ cuộc</Badge>
                                                        ) : detail.player_3 ? (
                                                            <div>
                                                                <Link
                                                                    to={`/players/${detail.player_3}`}
                                                                    style={{
                                                                        textDecoration: 'none',
                                                                        fontWeight: '600',
                                                                        color: 'inherit',
                                                                        transition: 'color 0.2s ease'
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.color = '#e67e22'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                                                                >
                                                                    {detail.p3_name || "-"}
                                                                </Link>
                                                                <div className="d-flex align-items-center mt-1" style={{ fontSize: '0.8rem' }}>
                                                                    <Badge bg="secondary" className="me-1 fw-normal" style={{ fontSize: '0.75rem' }}>
                                                                        {p3HistoricalLevel || "?"}
                                                                    </Badge>
                                                                    <span className="text-muted">
                                                                        {detail.point_player_3 !== null ? detail.point_player_3 : "-"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            detail.p3_name || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {isWalkover(detail.walkover) ? (
                                                            <span className="text-muted">-</span>
                                                        ) : detail.player_4 ? (
                                                            <div>
                                                                <Link
                                                                    to={`/players/${detail.player_4}`}
                                                                    style={{
                                                                        textDecoration: 'none',
                                                                        fontWeight: '600',
                                                                        color: 'inherit',
                                                                        transition: 'color 0.2s ease'
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.color = '#e67e22'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                                                                >
                                                                    {detail.p4_name || "-"}
                                                                </Link>
                                                                <div className="d-flex align-items-center mt-1" style={{ fontSize: '0.8rem' }}>
                                                                    <Badge bg="secondary" className="me-1 fw-normal" style={{ fontSize: '0.75rem' }}>
                                                                        {p4HistoricalLevel || "?"}
                                                                    </Badge>
                                                                    <span className="text-muted">
                                                                        {detail.point_player_4 !== null ? detail.point_player_4 : "-"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            detail.p4_name || "-"
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge
                                                            bg={isMatchWin ? "success" : "danger"}
                                                            className="fs-6 px-3 py-2"
                                                            style={{ borderRadius: '8px' }}
                                                        >
                                                            {detail.home_result} - {detail.guest_result}
                                                        </Badge>
                                                        {isWalkover(detail.walkover) && (
                                                            <div className="small text-muted mt-1">
                                                                {getWalkoverLabel(detail.walkover)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {detail.video ? (
                                                            getYouTubeEmbedUrl(detail.video) ? (
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setCurrentVideoUrl(detail.video);
                                                                        setShowVideoModal(true);
                                                                    }}
                                                                    style={{ borderRadius: "6px" }}
                                                                    title="Xem Video"
                                                                >
                                                                    ▶️
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => window.open(detail.video, '_blank')}
                                                                    style={{ borderRadius: "6px" }}
                                                                    title="Mở link video"
                                                                >
                                                                    🔗
                                                                </Button>
                                                            )
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <Alert variant="info" className="mb-0 border-0" style={{ borderRadius: '12px' }}>
                                Không tìm thấy chi tiết trận con.
                            </Alert>
                        )
                    ) : (
                        <div className="text-center py-5">
                            <Spinner animation="border" className="mb-3" />
                            <p>Đang tải chi tiết trận đấu...</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseMatchDetailModal} style={{ borderRadius: '8px' }}>
                        Đóng
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Video Modal */}
            <Modal
                show={showVideoModal}
                onHide={() => {
                    setShowVideoModal(false);
                    setCurrentVideoUrl("");
                }}
                size="xl"
                centered
            >
                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor: "#e67e22",
                        color: "white",
                        borderRadius: "12px 12px 0 0",
                    }}
                >
                    <Modal.Title className="fw-bold">Video Trận Đấu</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0" style={{ borderRadius: "0 0 12px 12px" }}>
                    {currentVideoUrl ? (
                        <div
                            style={{
                                position: "relative",
                                width: "100%",
                                paddingBottom: "56.25%",
                                height: 0,
                                backgroundColor: "#000",
                            }}
                        >
                            <iframe
                                src={getYouTubeEmbedUrl(currentVideoUrl)}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title="Match Video"
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    border: 0,
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <p>Không có video để hiển thị.</p>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            <style>{`
        .hover-shadow:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(230, 126, 34, 0.2) !important;
        }
      `}</style>
        </Container>
    );
};

export default TeamDetail;
